import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {Button, ComboBox, DatePicker, EMPHASIS, Label, Tabs, Text, TextField} from '@forecast-it/design-system';
import {TaskSetDrawer} from '../../../forecast-app/project-tab/projects/scoping-page/task-set/TaskSetDrawer';
import {CreateTaskSetDrawer} from '../../../forecast-app/project-tab/projects/scoping-page/task-set/CreateTaskSetDrawer';
import {HoursInput} from '../../../forecast-app/shared/components/inputs/hours-input/hours_input_view';
import AssignedDropdown from '../../../forecast-app/shared/components/dropdowns/assigned-dropdown/assigned_dropdown';
import {createPhase, createTask} from '../../scheduling/scheduling_mutations';
import Util from '../../../forecast-app/shared/util/util';
import {hasPermission, hasTaskCreatePermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';

const TAB = {
	TASK: 'TASK',
	PHASE: 'PHASE',
	TASK_SET: 'TASK_SET',
};

const NAME_MAX_LENGTH = 191;

/** The form rows below the tab strip. Matches the drawer body's own 14px rhythm. */
const TabBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: 14px;
	padding-top: 14px;
`;

const FieldRow = styled.div`
	display: flex;
	gap: 12px;

	> * {
		flex: 1;
		min-width: 0;
	}
`;

const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

/**
 * The assigned dropdown, with its label rendered above it rather than passed in.
 *
 * The component draws its own label in grey all-caps -- the one label on this tab
 * not in the drawer's bold sentence case -- so it is supplied here as an ordinary
 * one instead. The catch: the component positions its assignee avatars
 * ABSOLUTELY, at a top offset that assumes its own label sits above the input.
 * With no label inside, that offset drops them past the input's bottom edge, so
 * it is corrected here to centre them in the 40px input: (40 - 26) / 2.
 */
const AssignedField = styled(Field)`
	&& .assigned-dropdown-wrapper .assigned-icons {
		top: 7px;
	}

	/* The avatar is a 13.28px block with a 6.64px CSS-border triangle above and
	   below it, so it needs 26.56px, and the box it sits in is 26px and hides its
	   overflow -- half a pixel off the bottom point, which reads as a blunt
	   hexagon. The box clips for name truncation, and the name is hidden here, so
	   letting it show costs nothing. */
	&& .assigned-dropdown-wrapper .assigned-icons .forecast-person {
		overflow: visible;
	}

	/* The tab content this form sits in sets line-height: 24px, and the avatar's
	   initials carry none of their own, so the text box inherits 24px while the
	   hexagon's middle band is 13.28px. Anchored to the top of that band, the
	   letters land about 5px low, and the triangle drawn beneath the band paints
	   over them -- the initials are sheared off flat. Everywhere else in the app
	   this avatar inherits line-height: normal, so that is what it gets back. */
	&& .person-initials-hexagon-shape .initials {
		line-height: normal;
	}
`;

/** A single-select over items that always have a value, e.g. the project. */
const Picker = ({label, items, value, onChange, dataCy}) => (
	<Field>
		<Label>{label}</Label>
		<ComboBox
			aria-label={label}
			data-cy={dataCy}
			defaultItems={items.map(item => ({id: item.id, name: item.name}))}
			value={value ?? ''}
			onChange={onChange}
			width="flex"
		>
			{item => (
				<ComboBox.Item textValue={item.name}>
					<Text noWrap>{item.name}</Text>
				</ComboBox.Item>
			)}
		</ComboBox>
	</Field>
);

const NONE = '__none__';

/** The same, with an explicit "none" entry for values the task may go without. */
const OptionalPicker = ({label, noneLabel, items, value, onChange, dataCy}) => (
	<Picker
		label={label}
		items={[{id: NONE, name: noneLabel}, ...items]}
		value={value ?? NONE}
		onChange={next => onChange(next === NONE ? null : next)}
		dataCy={dataCy}
	/>
);

const dayFrom = iso => Number((iso || '').slice(8, 10));
const monthFrom = iso => Number((iso || '').slice(5, 7));
const yearFrom = iso => Number((iso || '').slice(0, 4));

/**
 * The drawer that replaces the scheduling create-new modal on the project
 * timeline: one surface with a Task tab, a Phase tab and a Task set tab, opened
 * by the same drag that opened the modal, carrying the drag's dates. Task and
 * phase creation call the same mutations with the same payload the modal sent,
 * so the result in the UI is identical; the task set tab embeds the scoping
 * page's create-task-set form.
 *
 * The controls are the design-system set the task-set form uses, so the three
 * tabs read as one surface -- except the task tab's Assigned field, which is the
 * app's own assigned dropdown because a task carries one role plus any number of
 * people and no design-system control covers that pair.
 *
 * `payload` is the object the modal received: projects, phases, roles, persons,
 * projectPersons, the drag's start and end moments, the group's project and
 * phase, and a `drawerOpenId` stamped per open so a new drag resets the form.
 */
export const SchedulingCreateDrawer = ({payload, labels = [], companyWorkingHours = null, onClose}) => {
	const {formatMessage} = useIntl();
	const isOpen = !!payload;

	const canCreateTask = hasTaskCreatePermission();
	const canCreatePhase = !!payload?.isProjectScheduling && hasPermission(PERMISSION_TYPE.PHASE_CREATE);
	const initialTab = payload?.isPhaseTabSelected && canCreatePhase ? TAB.PHASE : canCreateTask ? TAB.TASK : TAB.PHASE;

	const [tab, setTab] = useState(initialTab);
	const [name, setName] = useState('');
	// Hours arrive from the hours input as a float on blur; points are held as
	// typed and converted once at the boundary, matching the modal's own split.
	const [estimateHours, setEstimateHours] = useState(null);
	const [estimateEntered, setEstimateEntered] = useState('');
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [datesSeededFor, setDatesSeededFor] = useState(null);
	const [selectedProjectId, setSelectedProjectId] = useState(payload?.selectedProjectId || null);
	const [selectedPhaseId, setSelectedPhaseId] = useState(payload?.selectedPhaseId || null);
	// The role object rather than its id, and the person objects rather than their
	// ids: the assigned dropdown renders names and avatars off them, and a task
	// carries one role with any number of people.
	const [assignedRole, setAssignedRole] = useState(null);
	const [assignedPersons, setAssignedPersons] = useState([]);
	const [submitting, setSubmitting] = useState(false);

	// Each drag opens the drawer with its own dates and group; the form starts
	// from that drag, not from whatever the last one left behind. The modal got
	// this for free by mounting fresh each time; the drawer stays mounted for its
	// slide animation, so it resets explicitly.
	useEffect(() => {
		if (!payload) {
			return;
		}
		setTab(payload.isPhaseTabSelected && canCreatePhase ? TAB.PHASE : canCreateTask ? TAB.TASK : TAB.PHASE);
		setName('');
		setEstimateHours(null);
		setEstimateEntered('');
		setSelectedProjectId(payload.selectedProjectId || null);
		setSelectedPhaseId(payload.selectedPhaseId || null);
		setAssignedRole(null);
		setAssignedPersons([]);
		setSubmitting(false);
	}, [payload?.drawerOpenId]);

	// The dates are seeded during render rather than in the effect above, because
	// the task set form mounts in the same commit the drag arrives and reads them
	// as its own starting point. An effect runs after that, so it would mount
	// unseeded -- an empty Starts, and an Ends left on the count bound because no
	// end date was there to switch it. This is the documented way to adjust state
	// when a prop changes: React re-renders before showing the children anything.
	if (payload && payload.drawerOpenId !== datesSeededFor) {
		setDatesSeededFor(payload.drawerOpenId);
		setStartDate(payload.startDate ? payload.startDate.format('YYYY-MM-DD') : '');
		setEndDate(payload.endDate ? payload.endDate.format('YYYY-MM-DD') : '');
	}

	const selectedProject = isOpen ? payload.projects.find(project => project.id === selectedProjectId) : null;
	// No task sets on a project linked to Sage Intacct. The scheduling data flags each
	// project, so this follows whichever one is selected, the dragged row's or another.
	const taskSetBlockedBySage = !!selectedProject?.isSageLinked;
	const estimationUnit = selectedProject ? selectedProject.estimationUnit : null;
	const selectedPhase = isOpen ? payload.phases.find(phase => phase.id === selectedPhaseId) : null;

	const activePersons = isOpen ? payload.persons.filter(person => person.active && person.clientId === undefined) : [];

	// The assigned dropdown takes {node: {...}} edges, and pairs each person with
	// their role so the list reads "name, role". Same shapes the modal built.
	const assignablePersonOptions = activePersons.map(person => ({
		node: {person, role: payload.roles.find(role => role.id === person.roleId)},
	}));
	const assignableRoleOptions = isOpen
		? payload.roles.filter(role => role.name).map(role => ({node: {id: role.id, name: role.name}}))
		: [];
	// Without permission to add someone to a project, only people already on the
	// selected project can be assigned -- assigning anyone else would have to add
	// them first.
	const projectPersonOptions =
		isOpen && selectedProjectId
			? payload.projectPersons
					.filter(projectPerson => projectPerson.projectId === selectedProjectId)
					.map(projectPerson => {
						const person = activePersons.find(p => p.id === projectPerson.personId);
						return person
							? {node: {person, role: payload.roles.find(role => role.id === projectPerson.roleId)}}
							: null;
					})
					.filter(Boolean)
			: [];

	// The selected project's own columns, in board order, so the set form opens on
	// the project's first column exactly as it does on the scoping page. Follows
	// the picker rather than the drag, because the project can be changed here.
	const projectStatusColumns =
		isOpen && selectedProjectId
			? (payload.statusColumns || [])
					.filter(statusColumn => statusColumn.projectId === selectedProjectId)
					.slice()
					.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
			: [];

	const canCreate = !!name.trim() && !!selectedProject && !!startDate && !!endDate && !submitting;

	const create = () => {
		if (!canCreate) return;

		const onSuccess = response => {
			Util.dispatchScheduleEvent(response);
			onClose();
		};

		const mutationObject = {
			projectId: selectedProjectId,
			name,
			deadlineDay: dayFrom(endDate),
			deadlineMonth: monthFrom(endDate),
			deadlineYear: yearFrom(endDate),
			startDay: dayFrom(startDate),
			startMonth: monthFrom(startDate),
			startYear: yearFrom(startDate),
			csrfToken: Util.getCsrfValue(),
			socketClientId: Util.getClientId(),
		};

		setSubmitting(true);
		if (tab === TAB.PHASE) {
			createPhase(mutationObject, onSuccess);
		} else {
			// The task tab is hidden without the permission, but create serves both
			// tabs, so the task branch re-checks rather than returning early up top.
			if (!hasTaskCreatePermission()) return;
			mutationObject.forecast =
				estimationUnit === 'POINTS'
					? Math.round(parseFloat(estimateEntered || 0) * 100) / 100
					: Util.getHourEstimate((estimateHours ?? 0) * 60);
			mutationObject.phaseId = selectedPhaseId;
			mutationObject.roleId = assignedRole ? assignedRole.id : null;
			mutationObject.assignedPersons = assignedPersons.map(person => person.id);
			createTask(mutationObject, onSuccess);
		}
	};

	const projectField = isOpen ? (
		<Picker
			label={formatMessage({id: 'common.project'})}
			items={payload.projects.filter(project => project.name)}
			value={selectedProjectId}
			onChange={projectId => {
				setSelectedProjectId(projectId);
				setSelectedPhaseId(null);
			}}
			dataCy="scheduling-create-drawer-project"
		/>
	) : null;

	const datesRow = (
		<FieldRow>
			<DatePicker label={formatMessage({id: 'common.start_date'})} value={startDate} onChange={setStartDate} />
			<DatePicker label={formatMessage({id: 'common.end_date'})} value={endDate} onChange={setEndDate} />
		</FieldRow>
	);

	return (
		<TaskSetDrawer
			isOpen={isOpen}
			onClose={onClose}
			title={formatMessage({id: 'scheduling.create_new'})}
			subtitle={selectedProject ? selectedProject.name : undefined}
			footer={
				tab === TAB.TASK_SET ? (
					// The task-set form brings its own footer. The Sage notice that replaces it
					// does not, so it gets Cancel alone: the drawer's Create would make a task.
					taskSetBlockedBySage ? (
						<Button emphasis={EMPHASIS.MEDIUM} onPress={onClose} data-cy="scheduling-create-drawer-cancel">
							{formatMessage({id: 'common.cancel'})}
						</Button>
					) : undefined
				) : (
					<>
						<Button emphasis={EMPHASIS.MEDIUM} onPress={onClose} data-cy="scheduling-create-drawer-cancel">
							{formatMessage({id: 'common.cancel'})}
						</Button>
						<Button
							emphasis={EMPHASIS.HIGH}
							disabled={!canCreate}
							onPress={create}
							data-cy="scheduling-create-drawer-create"
						>
							{formatMessage({id: 'common.create'})}
						</Button>
					</>
				)
			}
		>
			{isOpen ? (
				<Tabs value={tab} onValueChange={setTab}>
					<Tabs.List>
						{canCreateTask ? (
							<Tabs.Tab value={TAB.TASK} data-cy="scheduling-create-drawer-tab-task">
								{formatMessage({id: 'common.task'})}
							</Tabs.Tab>
						) : null}
						{canCreatePhase ? (
							<Tabs.Tab value={TAB.PHASE} data-cy="scheduling-create-drawer-tab-phase">
								{formatMessage({id: 'common.scope-group'})}
							</Tabs.Tab>
						) : null}
						{canCreateTask ? (
							<Tabs.Tab value={TAB.TASK_SET} data-cy="scheduling-create-drawer-tab-task-set">
								{formatMessage({id: 'common.task_set'})}
							</Tabs.Tab>
						) : null}
					</Tabs.List>

					<Tabs.Content value={TAB.TASK}>
						<TabBody>
							<FieldRow>
								{projectField}
								<OptionalPicker
									label={formatMessage({id: 'common.scope-group'})}
									noneLabel={formatMessage({id: 'project_scopes.no-scope'})}
									items={payload.phases.filter(phase => phase.projectId === selectedProjectId)}
									value={selectedPhaseId}
									onChange={setSelectedPhaseId}
									dataCy="scheduling-create-drawer-phase"
								/>
							</FieldRow>
							{datesRow}
							<TextField
								label={formatMessage({id: 'common.task_name'})}
								value={name}
								onValueChange={setName}
								maxLength={NAME_MAX_LENGTH}
								width="flex"
								data-cy="scheduling-create-drawer-task-name"
							/>
							{/* Assignees and role are separate fields, the way the task modal
							    presents them: it calls this same control twice, once people-only
							    and once role-only. Bundling both into one control was this
							    drawer's own invention and made the two tabs read differently.
							    Each label is supplied from outside because the control draws its
							    own in grey uppercase -- see AssignedField. */}
							<FieldRow>
								<AssignedField>
									<Label>{formatMessage({id: 'common.assignees'})}</Label>
									<AssignedDropdown
										assignablePersons={
											hasPermission(PERMISSION_TYPE.PROJECT_PERSON_CREATE)
												? assignablePersonOptions
												: projectPersonOptions
										}
										assignedPersons={assignedPersons}
										assignPerson={personIds =>
											setAssignedPersons(
												personIds
													.map(personId => activePersons.find(person => person.id === personId))
													.filter(Boolean)
											)
										}
										unassignPerson={personId =>
											setAssignedPersons(assignedPersons.filter(person => person.id !== personId))
										}
										isMultiSelect={true}
										disabled={!hasPermission(PERMISSION_TYPE.PROJECT_PERSON_CREATE) && !selectedProjectId}
										projectId={selectedProjectId}
										showSuggestions={false}
										cy="scheduling-create-drawer-assignees"
									/>
								</AssignedField>
								<AssignedField>
									<Label>{formatMessage({id: 'common.role'})}</Label>
									<AssignedDropdown
										assignableRoles={assignableRoleOptions}
										assignedRole={assignedRole}
										assignRole={roleIdToAssign =>
											setAssignedRole(
												roleIdToAssign
													? payload.roles.find(role => role.id === roleIdToAssign) ?? null
													: null
											)
										}
										isMultiSelect={false}
										projectId={selectedProjectId}
										showSuggestions={false}
										cy="scheduling-create-drawer-role"
									/>
								</AssignedField>
							</FieldRow>
							<FieldRow>
								{estimationUnit === 'POINTS' ? (
									<TextField
										label={formatMessage({id: 'common.estimate'})}
										value={estimateEntered}
										onValueChange={setEstimateEntered}
										width="flex"
										data-cy="scheduling-create-drawer-estimate-points"
									/>
								) : (
									<Field>
										<Label>{formatMessage({id: 'common.estimate'})}</Label>
										<HoursInput
											replicateDesignSystem
											width="100%"
											placeholder={`0${formatMessage({id: 'common.hours.short'})}`}
											value={estimateHours ?? 0}
											mutation={value =>
												setEstimateHours(
													typeof value === 'number' && isFinite(value) && value > 0 ? value : null
												)
											}
											cy="scheduling-create-drawer-estimate-hours"
										/>
									</Field>
								)}
							</FieldRow>
						</TabBody>
					</Tabs.Content>

					<Tabs.Content value={TAB.PHASE}>
						<TabBody>
							{projectField}
							{datesRow}
							<TextField
								label={formatMessage({id: 'scheduling.phase_name'})}
								value={name}
								onValueChange={setName}
								maxLength={NAME_MAX_LENGTH}
								width="flex"
								data-cy="scheduling-create-drawer-phase-name"
							/>
						</TabBody>
					</Tabs.Content>

					{/* forceMount: the other two tabs keep their fields' values on the drawer
					    itself, so only their inputs go when the panel does. This one holds its
					    state inside the form, which an unmounted panel takes with it -- a name
					    typed here came back empty, and a hand-edited date came back re-seeded
					    from the drag, which read as the tab quietly undoing the edit. */}
					<Tabs.Content value={TAB.TASK_SET} forceMount>
						<TabBody>
							{/* The scoping page's form, without its own shell. It creates the
							    set against whichever project and phase the Task tab has
							    selected, and offers that project's own status columns, so a
							    set created here reads the same as one created from scoping.
							    The form opens on the project's first column, which is also
							    what the backend would apply on its own. */}
							{/* The dates are the drawer's, not this tab's: all three tabs describe
							    the same piece of work, so the range the user dragged -- and any
							    change to it afterwards, made on whichever tab -- is one answer
							    shown three times. The drag's end bounds the last date an instance
							    may START on; a deadline may still fall past it. */}
							{taskSetBlockedBySage ? (
								// The project stays changeable here, as on the other tabs: picking
								// one that is not linked brings the form back.
								<>
									{projectField}
									<Text data-cy="scheduling-create-drawer-task-set-sage-linked">
										{formatMessage({id: 'task_set.sage_linked_project'})}
									</Text>
								</>
							) : (
								<CreateTaskSetDrawer
									key={payload.drawerOpenId}
									embedded
									isOpen
									projectId={selectedProjectId}
									phaseId={selectedPhaseId}
									projectName={selectedProject ? selectedProject.name : undefined}
									phaseName={selectedPhase ? selectedPhase.name : undefined}
									isPointsProject={estimationUnit === 'POINTS'}
									roles={payload.roles}
									assignablePersons={activePersons}
									statusColumns={projectStatusColumns}
									seedDate={startDate || null}
									boundEndDate={endDate || null}
									onSeedDateChange={setStartDate}
									onBoundEndDateChange={setEndDate}
									labels={labels}
									companyWorkingHours={companyWorkingHours}
									projectBudgetType={selectedProject ? selectedProject.budgetType : null}
									onClose={onClose}
								/>
							)}
						</TabBody>
					</Tabs.Content>
				</Tabs>
			) : null}
		</TaskSetDrawer>
	);
};
